import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard } from '../services/dashboardService';
import { GENDER } from '../constants/enums';

const genderMap = GENDER;

function getUserStatus(u) {
  if (u.isBlocked === 1) return 'Blocked';
  if (u.isActive === 0) return 'Inactive';
  return 'Active';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function UserGrowthChart({ chartData }) {
  const canvasRef = useRef(null);
  const pointsRef = useRef([]);
  const hoverIndexRef = useRef(-1);
  const [tooltip, setTooltip] = useState(null);

  const drawChart = useCallback((hoverIndex = -1) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const w = rect.width;
    const h = rect.height;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartW = w - padding.left - padding.right;
    const chartH = h - padding.top - padding.bottom;

    const data = chartData.values || [];
    const xLabels = chartData.labels || [];
    const tooltipLabels = chartData.tooltipLabels || xLabels;
    if (data.length === 0) {
      pointsRef.current = [];
      return;
    }

    // Inset points from the chart edges so dots don't touch the y-axis or
    // right border (very visible with few points, e.g. the Today view)
    const inset = Math.min(40, chartW / (data.length * 2));
    const pointX = (i) =>
      data.length === 1
        ? padding.left + chartW / 2
        : padding.left + inset + (i / (data.length - 1)) * (chartW - inset * 2);

    const maxVal = Math.max(...data, 1);
    const niceMax = Math.ceil(maxVal / 5) * 5 || 5;
    const yStepCount = 5;
    const ySteps = Array.from({ length: yStepCount + 1 }, (_, i) => Math.round((niceMax / yStepCount) * i));
    const pointY = (val) => padding.top + chartH - (val / niceMax) * chartH;

    pointsRef.current = data.map((val, i) => ({
      x: pointX(i),
      y: pointY(val),
      value: val,
      label: tooltipLabels[i] || xLabels[i] || '',
    }));

    ctx.clearRect(0, 0, w, h);

    // Grid lines — uniform faint horizontal lines
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ySteps.forEach((val) => {
      const y = pointY(val);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(w - padding.right, y);
      ctx.stroke();
    });

    // Y-axis labels (skip the 0 baseline label, per design)
    ctx.fillStyle = '#9b9ba6';
    ctx.font = '11px Inter, sans-serif';
    ctx.textAlign = 'right';
    ySteps.forEach((val) => {
      if (val === 0) return;
      ctx.fillText(val, padding.left - 10, pointY(val) + 4);
    });

    // X-axis labels
    ctx.textAlign = 'center';
    xLabels.forEach((label, i) => {
      ctx.fillText(label, pointX(i), h - 10);
    });

    // Area fill under the line
    if (data.length > 1) {
      const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
      gradient.addColorStop(0, 'rgba(64,78,110,0.35)');
      gradient.addColorStop(1, 'rgba(64,78,110,0)');
      ctx.beginPath();
      data.forEach((val, i) => {
        if (i === 0) ctx.moveTo(pointX(i), pointY(val));
        else ctx.lineTo(pointX(i), pointY(val));
      });
      ctx.lineTo(pointX(data.length - 1), pointY(0));
      ctx.lineTo(pointX(0), pointY(0));
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Line
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      data.forEach((val, i) => {
        if (i === 0) ctx.moveTo(pointX(i), pointY(val));
        else ctx.lineTo(pointX(i), pointY(val));
      });
      ctx.stroke();
    }

    // Solid white dots at each data point
    data.forEach((val, i) => {
      const x = pointX(i);
      const y = pointY(val);
      if (i === hoverIndex) {
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
    });
  }, [chartData]);

  useEffect(() => {
    drawChart(-1);
    hoverIndexRef.current = -1;
    setTooltip(null);

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const observer = new ResizeObserver(() => drawChart(hoverIndexRef.current));
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [drawChart]);

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const points = pointsRef.current;
    if (points.length === 0) return;

    // Prefer the nearest point by X so hovering along the chart is easy,
    // then require the cursor to be reasonably close vertically.
    let nearest = -1;
    let nearestDx = Infinity;
    points.forEach((p, i) => {
      const dx = Math.abs(p.x - mx);
      if (dx < nearestDx) {
        nearestDx = dx;
        nearest = i;
      }
    });

    const point = points[nearest];
    const xThreshold = points.length > 1
      ? Math.max(20, Math.abs(points[1].x - points[0].x) / 2)
      : 30;
    const inRange = nearestDx <= xThreshold && Math.abs(point.y - my) <= 48;

    if (!inRange) {
      if (hoverIndexRef.current !== -1) {
        hoverIndexRef.current = -1;
        drawChart(-1);
        setTooltip(null);
      }
      return;
    }

    if (hoverIndexRef.current !== nearest) {
      hoverIndexRef.current = nearest;
      drawChart(nearest);
    }
    setTooltip({
      x: point.x,
      y: point.y,
      label: point.label,
      value: point.value,
    });
  };

  const handleMouseLeave = () => {
    hoverIndexRef.current = -1;
    drawChart(-1);
    setTooltip(null);
  };

  return (
    <div className="chart-canvas-wrap">
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
      {tooltip && (
        <div
          className="chart-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <span className="chart-tooltip-label">{tooltip.label}</span>
          <span className="chart-tooltip-value">{tooltip.value.toLocaleString()} users</span>
        </div>
      )}
    </div>
  );
}

const chartPeriods = [
  { key: 'today', label: 'Today' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

function formatHourLabel(label) {
  if (!label || !/^\d{2}:\d{2}$/.test(label)) return label || '';
  const hour = Number(label.split(':')[0]);
  if (hour === 0) return '12a';
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return '12p';
  return `${hour - 12}p`;
}

function formatDateLabel(label) {
  if (!label) return '';
  const date = new Date(`${label}T00:00:00`);
  if (Number.isNaN(date.getTime())) return label;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonthLabel(label) {
  if (!label) return '';
  const [year, month] = label.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  if (Number.isNaN(date.getTime())) return label;
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function buildChartData(data) {
  if (data.registrationsByToday?.length) {
    const points = data.registrationsByToday;
    return {
      values: points.map((d) => d.count),
      labels: points.map((d, i) => (i % 4 === 0 ? formatHourLabel(d.label) : '')),
      tooltipLabels: points.map((d) => formatHourLabel(d.label) || d.label),
    };
  }

  if (data.registrationsByWeekly?.length) {
    const points = data.registrationsByWeekly;
    return {
      values: points.map((d) => d.count),
      labels: points.map((d) => formatDateLabel(d.label)),
    };
  }

  if (data.registrationsByMonth?.length) {
    const points = data.registrationsByMonth;
    return {
      values: points.map((m) => m.count),
      labels: points.map((m) => formatMonthLabel(m.label || m.month)),
    };
  }

  if (data.registrationsByYearly?.length) {
    const points = data.registrationsByYearly;
    return {
      values: points.map((d) => d.count),
      labels: points.map((d) => d.label || ''),
    };
  }

  // Legacy field names
  if (data.registrationsByDay?.length) {
    const points = [...data.registrationsByDay].reverse();
    return {
      values: points.map((d) => d.count),
      labels: points.map((d) => formatDateLabel(d.date || d.day || d.label)),
    };
  }

  if (data.registrationsByWeek?.length) {
    const points = [...data.registrationsByWeek].reverse();
    return {
      values: points.map((w) => w.count),
      labels: points.map((w) => w.label || w.week || ''),
    };
  }

  return { values: [], labels: [] };
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState([]);
  const [chartData, setChartData] = useState({ values: [], labels: [] });
  const [recentUsers, setRecentUsers] = useState([]);
  const [period, setPeriod] = useState('monthly');

  const fetchDashboard = useCallback(async () => {
    setError('');
    try {
      const response = await getDashboard(period);
      const data = response.data || response;

      // Stats
      const s = data.stats || {};
      const revenue = s.totalRevenue?.amount ?? 0;
      setStats([
        { label: 'Total Users', value: s.totalUsers?.count ?? 0, change: s.totalUsers?.changePercent ?? 0, up: (s.totalUsers?.changePercent ?? 0) >= 0, icon: `${process.env.PUBLIC_URL}/images/total-users.svg` },
        { label: 'Active Users', value: s.activeUsers?.count ?? 0, change: s.activeUsers?.changePercent ?? 0, up: (s.activeUsers?.changePercent ?? 0) >= 0, icon: `${process.env.PUBLIC_URL}/images/active-users.svg` },
        { label: 'Total Matches', value: s.totalMatches?.count ?? 0, change: s.totalMatches?.changePercent ?? 0, up: (s.totalMatches?.changePercent ?? 0) >= 0, icon: `${process.env.PUBLIC_URL}/images/total-matched.svg` },
        {
          label: 'Total Revenue',
          value: typeof revenue === 'number'
            ? `$${revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : revenue,
          change: s.totalRevenue?.changePercent ?? 0,
          up: (s.totalRevenue?.changePercent ?? 0) >= 0,
          icon: `${process.env.PUBLIC_URL}/images/total-revenue.svg`,
        },
      ]);

      // Recent users
      setRecentUsers(data.recentUsers || []);

      // Chart — registrations by selected period (sorted oldest to newest)
      setChartData(buildChartData(data));
    } catch (err) {
      setError(err.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return <div className="loading-screen">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="loading-screen">{error}</div>;
  }

  return (
    <div className="dashboard">
      <div className="stats-grid">
        {stats.map((stat) => (
          <div className="stat-card" key={stat.label}>
            <div className="stat-icon">
              <img src={stat.icon} alt={stat.label} />
            </div>
            <div className="stat-info">
              <span className="stat-label">{stat.label}</span>
              <span className="stat-value">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</span>
              {stat.change !== '' && (
                <span className={`stat-change ${stat.up ? 'up' : 'down'}`}>
                  ~{stat.change}% <span className="stat-change-text">From last month</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="tables-grid">
        <div className="chart-card">
          <div className="chart-card-header">
            <h3>User Growth</h3>
            <div className="chart-tabs">
              {chartPeriods.map((p) => (
                <button
                  key={p.key}
                  className={`chart-tab${period === p.key ? ' active' : ''}`}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-container">
            <UserGrowthChart chartData={chartData} />
          </div>
        </div>

        <div className="table-card">
          <div className="table-card-header">
            <h3>Recent Users</h3>
            <Link to="/users" className="view-all-link">View All</Link>
          </div>
          <table className="mini-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Gender</th>
                <th>Joined Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.length === 0 ? (
                <tr><td colSpan="5" className="users-empty">No recent users.</td></tr>
              ) : (
                recentUsers.map((u) => {
                  const status = getUserStatus(u);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {u.image?.url ? (
                              <img
                                src={u.image.url}
                                alt={u.name || 'User'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = (u.name || '?').charAt(0).toUpperCase();
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {(u.name || '?').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          {u.name}
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>{genderMap[u.gender] || '-'}</td>
                      <td>{formatDate(u.createdAt)}</td>
                      <td>
                        <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
